import { Component, EventEmitter, inject, Input, Output, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
  ReactiveFormsModule
} from '@angular/forms';
import { FeedbackConfig, FeedbackSubmission } from '../models/feedback.models';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { Rating } from 'primeng/rating';
import { ChipsModule } from 'primeng/chips';
import { FeedbackService } from '../services/feedback-service.service';
import { Chip } from 'primeng/chip';
import { NgClass } from '@angular/common';
import { AmplitudeService, LogAmplitudeEventDirective } from '@brighthub/logging';

@Component({
  selector: 'lib-feedback-dialog',
  standalone: true,
  imports: [
    Button,
    Dialog,
    ReactiveFormsModule,
    Rating,
    ChipsModule,
    Chip,
    LogAmplitudeEventDirective,
  ],
  templateUrl: './feedback-dialog.component.html',
  styleUrls: ['./feedback-dialog.component.scss'],
})
export class FeedbackDialogComponent implements OnInit {
  private _feedbackService = inject(FeedbackService);
  private _amplitude = inject(AmplitudeService);

  @Input() feedbackProps: FeedbackConfig | null = null;
  @Input() visible = true;
  @Output() submitFeedback = new EventEmitter<FeedbackSubmission>();
  @Output() closeDialog = new EventEmitter<void>();
  @Output() canceled = new EventEmitter<void>();

  feedbackForm: FormGroup;

  constructor(private _fb: FormBuilder) {
    this.feedbackForm = this._fb.group(
      {
        rating: [null],
        comment: [''],
        userTags: [[]],
      },
      { validators: [this._requireRatingOrComment()] }
    );
  }

  ngOnInit(): void {
    if (this.feedbackProps?.userTags?.length) {
      this.feedbackForm.patchValue({
        userTags: [...this.feedbackProps.userTags],
      });
    }
  }

  get titleText(): string {
    return this.feedbackProps?.titleText || 'How did we do?';
  }

  get commentText(): string {
    return (
      this.feedbackProps?.commentText || 'Any feedback to help us improve?'
    );
  }

  private _requireRatingOrComment(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const rating = group.get('rating')?.value;
      const comment = group.get('comment')?.value?.trim();
      const tags = group.get('userTags')?.value ?? [];
      return rating !== null || comment || tags.length > 0
        ? null
        : { required: true };
    };
  }

  onSubmit(): void {
    if (this.feedbackForm.valid) {
      const payload: FeedbackSubmission = {
        ...this.feedbackForm.value,
        ...this.feedbackProps,
        userTags: this.feedbackForm.value.userTags,
      };

      this._feedbackService.submitFeedback(payload).subscribe({
        next: () => {
          this.submitFeedback.emit(payload);
          this.onClose();
        },
        error: () => {
          // handled in service
        },
      });
    }
  }
  isTagSelected(tag: string): boolean {
    return this.feedbackForm.get('userTags')?.value?.includes(tag);
  }
  toggleTag(tag: string): void {
    const tags: string[] = this.feedbackForm.get('userTags')?.value ?? [];
    const index = tags.indexOf(tag);
    if (index > -1) {
      tags.splice(index, 1);
    } else {
      tags.push(tag);
    }
    this.feedbackForm.patchValue({ userTags: [...tags] });
  }

  onClose(): void {
    this.visible = false;
    this.closeDialog.emit();
  }

  onCancel(): void {
    this._amplitude.logEvent('feedback_dialog_canceled');
    this.visible = false;
    this.canceled.emit();
  }
}
